import { UnassignedItem } from 'src/entities/dispatch';

/**
 * 削除操作 1 回分のハンドル。呼び出し側は API の成否に応じて commit / rollback の
 * どちらかを 1 回だけ呼ぶ（どちらも呼ばないと、その操作は「未完了」のまま残る）。
 */
export type UnassignedRemovalHandle = {
  /**
   * サーバ反映が成功した。この操作を未完了から外す。
   * 「消した状態」の表示はここでは戻さない（サーバ値が追いつく前に戻すと、
   * 削除したはずの案件が refetch の間だけ画面に出戻る）。
   */
  commit: () => void;
  /**
   * サーバ反映が失敗した。この操作で消した分だけを差分で戻す
   * （全体スナップショットを戻すと、並行する別の削除まで復活してしまう）。
   */
  rollback: () => void;
};

/**
 * 未割当リストの楽観更新。API 応答前に画面から消し、失敗したらこの操作で消した分だけ戻す。
 * 機構は画面ごとに違う（カード配車は配列を上書き、地図配車は未確定操作から表示を導出）ので
 * 注入契約にしている。どちらの機構でも「未完了の操作が 0 になり、かつ確定分がサーバ値に
 * 反映されたら楽観状態を畳む」という終端の意味は共通。
 */
export type UnassignedOptimistic = {
  /** API 応答前に画面から消す。戻り値はこの操作専用のハンドル */
  remove: (removedItemIds: ReadonlySet<string>) => UnassignedRemovalHandle;
};

export type RemovalSnapshot = {
  /** この操作で取り除いた案件 */
  removedItems: UnassignedItem[];
  /** この操作で解除した選択 */
  removedSelectedIds: string[];
  /** remove 時点の全 id の並び順。復元位置の決定に使う */
  orderAtRemoval: string[];
};

/** この操作で取り除いた分だけを、現在の配列へ元の並び順で戻す */
export function restoreRemovedItems(
  current: UnassignedItem[],
  snapshot: RemovalSnapshot,
): UnassignedItem[] {
  const presentIds = new Set(current.map((i) => i.id));
  const toAdd = snapshot.removedItems.filter((item) => !presentIds.has(item.id));
  // 戻すものが無いときに並べ替えると、この操作と無関係な並び（他操作の復元順など）まで
  // 巻き戻してしまう。何もしないのが正しい
  if (toAdd.length === 0) return current;

  const merged = [...current, ...toAdd];
  // 元の並び順を rank にして並べ直す。index への splice だと先行要素が
  // 消えている場合に位置がずれる（[a,b,c] から B→A 削除して A 成功・B 失敗）
  const rank = new Map(snapshot.orderAtRemoval.map((id, i) => [id, i]));
  return merged.sort(
    (a, b) => (rank.get(a.id) ?? Number.MAX_SAFE_INTEGER) - (rank.get(b.id) ?? Number.MAX_SAFE_INTEGER),
  );
}

/** この操作で解除した分だけを選択に戻す */
export function restoreRemovedSelection(
  current: ReadonlySet<string>,
  snapshot: RemovalSnapshot,
): Set<string> {
  const next = new Set(current);
  snapshot.removedSelectedIds.forEach((id) => next.add(id));
  return next;
}
